import { createElement, useContext, useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { LeadSource, LeadStatus } from 'models/Leads';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from 'utils/localeUtils';
import { isLeadAMainBooking, isLeadASubBooking } from 'utils/lead/leadUtils';
import useAppUser from 'hooks/useAppUser';
import LeadModalInternalGuestNotesTab from './tabs/internalGuestNotes/LeadModaIInternalGuestNotesTab';
import LeadModalDetailsTab from './tabs/details/LeadModalDetailsTab';
import LeadModalClientInfoTab from './tabs/clientInfo/LeadModalClientInfoTab';
import LeadModalPaymentTimelineTab from './tabs/paymentTimeline/LeadModalPaymentTimelineTab';
import LeadModalStayDetailsTab from './tabs/stayDetailsTab/LeadModalStayDetailsTab';
import LeadModalDataTab from './tabs/dataTab/LeadModalDataTab';
import LeadModalScreenAndProtectTab from './tabs/screenAndProtect/LeadModalScreenAndProtectTab';
import LeadModalContext from './LeadModalContext';
import { LeadModalActionsTab } from './tabs/actionsTab/LeadModalActionsTab';
import useLeadScreenAndProtectDetails from './useLeadScreenAndProtectDetails';

export const LeadModalTabs = {
  leadDetails: 'leadDetails',
  clientInfo: 'clientInfo',
  paymentTimeline: 'paymentTimeline',
  stayDetails: 'stayDetails',
  data: 'data',
  internalGuestNotes: 'internalGuestNotes',
  screenAndProtect: 'screenAndProtect',
  actions: 'actions',
};

const newLeadTabs = [
  LeadModalTabs.leadDetails,
  LeadModalTabs.clientInfo,
  LeadModalTabs.stayDetails,
];

const tabNameToComponentMapping = {
  [LeadModalTabs.clientInfo]: LeadModalClientInfoTab,
  [LeadModalTabs.data]: LeadModalDataTab,
  [LeadModalTabs.internalGuestNotes]: LeadModalInternalGuestNotesTab,
  [LeadModalTabs.leadDetails]: LeadModalDetailsTab,
  [LeadModalTabs.paymentTimeline]: LeadModalPaymentTimelineTab,
  [LeadModalTabs.stayDetails]: LeadModalStayDetailsTab,
  [LeadModalTabs.screenAndProtect]: LeadModalScreenAndProtectTab,
  [LeadModalTabs.actions]: LeadModalActionsTab,
};

const LeadModalFormBodyTabs = () => {
  const { lead } = useContext(LeadModalContext);
  const [activeTab, setActiveTab] = useState(LeadModalTabs.leadDetails);
  const { t } = useTranslation();
  const updateMode = !!useWatch({ name: 'uid' });
  const { errors } = useFormState();
  const { isEmployeeManager, isEmployeeBookingAgent } = useAppUser();
  const { screenAndProtectDetails, isLoading: isLoadingScreenAndProtect } =
    useLeadScreenAndProtectDetails(lead?.uid);

  useEffect(() => {
    const {
      checkInDate,
      checkOutDate,
      email,
      phoneNumber_base,
      cellPhoneNumber_base,
    } = errors;

    if (checkInDate || checkOutDate) {
      setActiveTab(LeadModalTabs.leadDetails);
    } else if (email || phoneNumber_base || cellPhoneNumber_base) {
      setActiveTab(LeadModalTabs.clientInfo);
    }
  }, [errors]);

  // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const isMainBooking = isLeadAMainBooking(lead);

  const getTabNamesAvailableForLead = () => {
    let availableTabs = Object.values(LeadModalTabs);

    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    if (isLeadASubBooking(lead)) {
      availableTabs = availableTabs.filter(
        (key) =>
          key !== 'paymentTimeline' &&
          key !== 'internalGuestNotes' &&
          key !== 'actions',
      );
    }

    if (
      lead?.source !== LeadSource.DIRECT_BOOKINGDOTCOM ||
      !isEmployeeManager ||
      isMainBooking
    ) {
      availableTabs = availableTabs.filter((key) => key !== 'actions');
    }

    // only managers and bookingAgents can access the screen and protect tab
    const isUserAllowedToAccessScreenAndProtect =
      isEmployeeManager || isEmployeeBookingAgent;

    if (
      isLoadingScreenAndProtect ||
      !screenAndProtectDetails ||
      !isUserAllowedToAccessScreenAndProtect
    ) {
      availableTabs = availableTabs.filter((key) => key !== 'screenAndProtect');
    }

    return availableTabs;
  };

  const tabsToRender = updateMode ? getTabNamesAvailableForLead() : newLeadTabs;

  const isLeadPending = lead?.status === LeadStatus.PENDING;

  return (
    <Tabs
      activeKey={activeTab}
      animation={false}
      onSelect={(tabName) => setActiveTab(tabName)}
    >
      {tabsToRender.map((tabName) => (
        <Tab
          data-testid={`lead-form-tab-${tabName}`}
          eventKey={tabName}
          key={tabName}
          title={t(normalizeKey(`componentLead.modal.tabNames.${tabName}`))}
        >
          <fieldset disabled={isLeadPending}>
            {createElement(tabNameToComponentMapping[tabName])}
          </fieldset>
        </Tab>
      ))}
    </Tabs>
  );
};

export default LeadModalFormBodyTabs;
